/**
 * ticketFormatoDemo.ts — Venta de EJEMPLO para comparar los formatos del ticket.
 *
 * QUÉ HACE:
 *  - Construye un payload `SaleTicketEscPosInput` (sin `paperWidthMm`) a partir
 *    del carrito de regresión (`buildTicketRegressionCart`), que ya cubre:
 *    productos de restaurante con modificadores (Desayuno Completo, Adiciones),
 *    variantes de precio extra (talla/color/material/capacidad), ítem por peso
 *    (Salchicha Zenú 3 kg), SKU y nombres largos.
 *  - Aplica descuento global 10% y servicio 15% con los MISMOS helpers que usa
 *    el cobro real (`computeCartDiscountBreakdown`) → los totales del ticket
 *    salen coherentes sin matemática manual.
 *  - Fuerza IVA 19% en el ítem «Papel Higiénico Familia x12» para que el ticket
 *    muestre la fila de impuesto global.
 *
 * POR QUÉ ES ESTÁNDAR:
 *  - No lleva puntos, crédito, devoluciones, anulaciones ni saldo → la regla
 *    `saleTicketSupportsCompact` lo permite → Compacto/Ultra aplican de verdad
 *    (incluso en la impresión real del ejemplo).
 *  - La `note` imprime la marca «EJEMPLO DE TICKET — NO ES UNA VENTA REAL» en
 *    los 3 formatos para que un papel impreso nunca parezca un recibo real.
 *
 * QUÉ EXPORTA: buildTicketFormatoDemoPayload, FormatoTicketDemoPayload (type).
 * QUÉ NO DEBE HACER: no persiste, no guarda ventas, no toca el carrito real.
 */
import type { Producto } from "@/types";
import type { SaleTicketEscPosInput } from "../utils/saleTicketEscPos";
import type { LineDiscount } from "../utils/discountHelper";
import { computeCartDiscountBreakdown } from "../utils/cartDiscountUtils";
import { all_category_products } from "@/mocks/data/products/index";
import {
  buildTicketRegressionCart,
  mergeRegressionCatalog,
} from "./ticketRegressionCart";

/** Payload listo para los builders; el ancho de papel lo agrega el caller. */
export type FormatoTicketDemoPayload = Omit<
  SaleTicketEscPosInput,
  "paperWidthMm"
>;

/** Descuento global del ejemplo (10%). */
const DEMO_GLOBAL_DISCOUNT: LineDiscount = { type: "%", value: 10 };

/** Porcentaje de servicio del ejemplo (15%). */
const DEMO_SERVICIO_PCT = 15;

/** Nombre exacto del ítem al que se le fuerza IVA (existe en cat_aseo demo). */
const DEMO_IVA_ITEM_NAME = "Papel Higiénico Familia x12";

/**
 * Construye la venta de ejemplo. Retorna `null` solo si el carrito de regresión
 * no resolvió ningún producto (catálogo demo vacío).
 */
export function buildTicketFormatoDemoPayload(): FormatoTicketDemoPayload | null {
  const products = mergeRegressionCatalog(all_category_products as Producto[]);
  const { items: rawItems } = buildTicketRegressionCart(products);
  if (rawItems.length === 0) return null;

  // Copia el carrito y fuerza IVA en un ítem para mostrar la fila de impuesto.
  const cart = rawItems.map((item) =>
    item.nombre === DEMO_IVA_ITEM_NAME
      ? { ...item, tieneIVA: true, porcentajeIVA: 19 }
      : item,
  );

  // Descuentos globales con el mismo helper del cobro real.
  const breakdown = computeCartDiscountBreakdown(cart, DEMO_GLOBAL_DISCOUNT);

  // Subtotal neto tras descuento → servicio sobre ese neto → total.
  const subtotal = Math.max(0, breakdown.total);
  const servicioAmount = Math.round((subtotal * DEMO_SERVICIO_PCT) / 100);
  const total = subtotal + servicioAmount;

  // Fecha estable a mediodía local (el ejemplo no debe "envejecer").
  const now = new Date();
  const ticketDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    12,
    30,
    0,
  );

  return {
    cart,
    subtotalBruto: breakdown.subtotalBruto,
    discountBreakdown: breakdown,
    globalDiscount: DEMO_GLOBAL_DISCOUNT,
    subtotal,
    servicioPct: DEMO_SERVICIO_PCT,
    servicioAmount,
    total,
    paymentMethod: "efectivo",
    cashReceived: total,
    change: 0,
    note: "EJEMPLO DE TICKET - NO ES UNA VENTA REAL",
    numeroVenta: "EJEMPLO",
    cajaNombre: "Caja principal",
    sesionAbiertaPor: "Ana García",
    registradoPor: "Juan Pérez",
    ticketDate,
  };
}
